import {
    PickCertDialogTitle as PickCertDialogTitle_1,
    PickCertToEncrypt as PickCertToEncrypt_1,
    PickCertToSign as PickCertToSign_1,
    PickCertToEncryptNotFound as PickCertToEncryptNotFound_1,
    PickCertToSignNotFound as PickCertToSignNotFound_1,
    PickEncryptCertToSign as PickEncryptCertToSign_1,
    TooLargeToBeAttached as TooLargeToBeAttached_1,
} from './getSmimeSettings.locstring.json';
import loc from 'owa-localize';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getPossibleSmimeCertSubjects, getAdminSettingsForSmime, calcCodePage } from './smimeUtils';
import SmimeAdminSettingsType from 'owa-service/lib/contract/SmimeAdminSettingsType';
import { SMIME_DEFAULT_CULTURE } from './constants';

type SmimeStrings = {
    PickCertDialogTitle: string;
    PickCertToEncrypt: string;
    PickCertToSign: string;
    PickCertToEncryptNotFound: string;
    PickCertToSignNotFound: string;
    PickEncryptCertToSign: string;
    TooLargeToBeAttached: string;
};

type SmimeSettingsType = {
    // The code page identifies the default encoding method to be used in this control.
    CodePage: number;

    // The subjects used to lookup a certificate.
    CertificateSubjects: string[];

    // The user's culture.
    Culture: string;

    // The limit of attachment size (the limit of the smime.p7m file).
    AttachmentSizeLimit: number;

    // Localized strings.
    Strings: SmimeStrings;

    // S/MIME Admin settings
    // The setting controls if it allows user choice of signing certificates.
    AllowUserChoiceOfSigningCertificate: boolean;

    // The setting controls if it includes certificate chain without root certificate.
    IncludeCertificateChainWithoutRootCertificate: boolean;

    // The setting controls if it includes certificate chain and root certificate.
    IncludeCertificateChainAndRootCertificate: boolean;

    // The setting controls if it encrypts temporary buffers.
    EncryptTemporaryBuffers: boolean;

    // Determines if the sender's encryption certificate will be included in emails.
    SignedEmailCertificateInclusion: boolean;

    // This setting controls if the Encryption and Signing algorithm attributes are attached to messages.
    IncludeSmimeCapabilitiesInMessage: boolean;

    // The setting controls if it places a copy of the From, To, Cc recipient headers in the signed portion of the message.
    CopyRecipientHeaders: boolean;

    // The setting controls if it forces user to use smart-card.
    OnlyUseSmartCard: boolean;

    // The setting controls if it encodes the lockbox where the asymmetrically encrypted token that is necessary to decrypt
    // the rest of the message is stored by indicating the issuer and serial number of each recipient's certificate.
    UseKeyIdentifier: boolean;

    // The list of encryption algorithms the admin want to use
    EncryptionAlgorithms: string;

    // The list of signing algorithms the admin want to use
    SigningAlgorithms: string;

    // Dump related
    // Indicates whether the control should create diagnostic dumps
    DiagnosticDumpsEnabled: boolean;

    // Owa control version
    OwaControlVersion: string;
};

const SmimeAdminSettingsDefaults: SmimeAdminSettingsType = {
    // Set up default values according to spec
    CheckCRLOnSend: false,
    DLExpansionTimeout: 60000,
    UseSecondaryProxiesWhenFindingCertificates: true,
    CRLConnectionTimeout: 60000,
    CRLRetrievalTimeout: 10000,
    DisableCRLCheck: false,
    AlwaysSign: false,
    AlwaysEncrypt: false,
    ClearSign: true,
    IncludeCertificateChainWithoutRootCertificate: false, // changed to false for consistency with config settings on TDS box
    IncludeCertificateChainAndRootCertificate: false,
    EncryptTemporaryBuffers: true,
    SignedEmailCertificateInclusion: true,
    BccEncryptedEmailForking: 0,
    IncludeSMIMECapabilitiesInMessage: false,
    CopyRecipientHeaders: false,
    OnlyUseSmartCard: false,
    TripleWrapSignedEncryptedMail: false, // changed to false because true results in Opaque Signed + Encrypted + Opaque Signed - which isn't handled by some Outlook addins
    UseKeyIdentifier: false, // changed to false for consistency with config settings on TDS box
    EncryptionAlgorithms: '6610', // AES256
    SigningAlgorithms: '8004', // SHA1
    ForceSmimeClientUpgrade: true,
    SenderCertificateAttributesToDisplay: '',
    AllowUserChoiceOfSigningCertificate: false,
};

const getSmimeStrings = (): SmimeStrings => {
    return {
        PickCertDialogTitle: loc(PickCertDialogTitle_1),
        PickCertToEncrypt: loc(PickCertToEncrypt_1),
        PickCertToSign: loc(PickCertToSign_1),
        PickCertToEncryptNotFound: loc(PickCertToEncryptNotFound_1),
        PickCertToSignNotFound: loc(PickCertToSignNotFound_1),
        PickEncryptCertToSign: loc(PickEncryptCertToSign_1),
        TooLargeToBeAttached: loc(TooLargeToBeAttached_1),
    };
};

const getSmimeSettings = (): SmimeSettingsType => {
    const userConfig = getUserConfiguration();

    const smimeAdminSettings = userConfig.SmimeAdminSettings
        ? userConfig.SmimeAdminSettings
        : SmimeAdminSettingsDefaults;
    const sessionSettings = userConfig.SessionSettings;

    const codePage: number = calcCodePage(userConfig.PolicySettings);

    const certificateSubjects = getPossibleSmimeCertSubjects(sessionSettings);
    const adminSettingsForSmime = getAdminSettingsForSmime(smimeAdminSettings);

    return {
        CodePage: codePage,
        Culture: sessionSettings.UserCulture || SMIME_DEFAULT_CULTURE,
        AttachmentSizeLimit: sessionSettings.MaxMessageSizeInKb * 1024,
        UseKeyIdentifier: false,
        CertificateSubjects: certificateSubjects,
        ...adminSettingsForSmime,
        Strings: getSmimeStrings(),
    } as SmimeSettingsType;
};

export default getSmimeSettings;
